import type { AdminBranch, LocalizedPair } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { Building2, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { BilingualField, OptionalBilingualField } from '@/components/bilingual';
import { FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { ImagePicker } from '@/components/image-picker';
import { PublishBlockers, RowActions, StatusBadge, TranslationBadge } from '@/components/records';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Pagination,
  SkeletonRows,
  Toolbar,
} from '@/components/ui';
import {
  apiPatch,
  apiPost,
  useAdminList,
  useAdminMutation,
  useLifecycle,
  useListParams,
  useReorder,
} from '@/hooks/admin';
import { useSettings } from '@/hooks/use-settings';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { ListFilters } from './_filters';

export const BRANCHES_URL = '/admin/branches';
/** The list this screen's form pages return to. */
export const BRANCHES_LIST = '/branches';
export const BRANCHES_INVALIDATE = [['branches'], ['branch'], qk.dashboard];

export function BranchesPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const [params, setParams] = useListParams();
  const list = useAdminList<AdminBranch>(qk.branches(params), BRANCHES_URL, params);
  const lifecycle = useLifecycle(BRANCHES_URL, BRANCHES_INVALIDATE);
  const reorder = useReorder(BRANCHES_URL, BRANCHES_INVALIDATE);

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const onLastPage = page * pageSize >= list.total;

  function reorderBy(id: string, delta: number) {
    if (!list.contentRevision) return;
    reorder.mutate({
      id,
      delta,
      visible: list.rows.map((row) => row.id),
      contentRevision: list.contentRevision,
    });
  }

  return (
    <>
      <PageHeader
        title={t('branches.title')}
        description={t('branches.subtitle')}
        actions={
          canEdit && (
            <Link to={`${BRANCHES_LIST}/new`} className="dio-btn dio-btn--primary">
              <Plus className="dio-icon" aria-hidden="true" />
              {t('branches.newTitle')}
            </Link>
          )
        }
      />

      <Toolbar>
        <ListFilters params={params} onChange={setParams} />
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState icon={<Building2 aria-hidden="true" />} title={t('branches.empty')} hint={t('branches.subtitle')} />
      ) : (
        <DataTable
          caption={t('branches.title')}
          columns={[
            <span className="sr-only">{t('common.image')}</span>,
            t('branches.name'),
            t('branches.timezone'),
            t('common.status'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {list.rows.map((row, index) => (
            <tr key={row.id}>
              <td style={{ inlineSize: '1%' }}>
                {row.image ? (
                  <img src={row.image.thumbUrl} alt="" className="dio-table__thumb" />
                ) : (
                  // A branch publishes without a photo on purpose — a branch
                  // card degrades fine — so this cell is often empty by design.
                  <span className="dio-table__thumb" aria-hidden="true" />
                )}
              </td>

              <td>
                <div className="dio-table__name flex flex-wrap items-center gap-2">
                  <span lang="ar" dir="rtl">
                    {row.name.ar || '—'}
                  </span>
                  <span className="dio-meta" aria-hidden="true">
                    /
                  </span>
                  <span lang="en" dir="ltr">
                    {row.name.en || '—'}
                  </span>
                </div>
                <TranslationBadge translation={row.translation} />
                {!row.hoursReadable && (
                  <p style={{ color: 'var(--color-danger-fg)' }}>{t('branches.hoursUnreadable')}</p>
                )}
              </td>

              <td className="dio-table__sub">{row.timezone ?? t('branches.timezoneHint')}</td>

              <td>
                <div className="flex flex-wrap items-center gap-1">
                  <StatusBadge status={row.publicationStatus} />
                  <OpenBadge isOpenNow={row.isOpenNow} />
                </div>
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  {canEdit && (
                    <>
                      <Button
                        size="sm"
                        iconOnly
                        variant="ghost"
                        aria-label={t('common.moveUp')}
                        disabled={
                          row.publicationStatus === 'ARCHIVED' ||
                          reorder.isPending ||
                          !list.contentRevision ||
                          (index === 0 && page === 1)
                        }
                        onClick={() => reorderBy(row.id, -1)}
                      >
                        <ChevronUp className="dio-icon" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        iconOnly
                        variant="ghost"
                        aria-label={t('common.moveDown')}
                        disabled={
                          row.publicationStatus === 'ARCHIVED' ||
                          reorder.isPending ||
                          !list.contentRevision ||
                          (index === list.rows.length - 1 && onLastPage)
                        }
                        onClick={() => reorderBy(row.id, 1)}
                      >
                        <ChevronDown className="dio-icon" aria-hidden="true" />
                      </Button>
                    </>
                  )}
                  {/* Hours are a screen of their own, and the one staff open
                      most on this list — so it stays a button, not a menu item. */}
                  <Link to={`/branches/${row.id}`} className="dio-btn dio-btn--secondary dio-btn--sm">
                    {t('hours.title')}
                  </Link>
                  <Link
                    to={`${BRANCHES_LIST}/${row.id}/edit`}
                    className="dio-btn dio-btn--secondary dio-btn--sm"
                  >
                    {canEdit ? t('common.edit') : t('common.details')}
                  </Link>
                  <RowActions
                    status={row.publicationStatus}
                    translation={row.translation}
                    canEdit={canEdit}
                    busy={lifecycle.busy}
                    label={t('common.openMenuFor', { name: row.name.ar || row.name.en })}
                    handlers={{
                      onPublish: () => lifecycle.publish.mutate({ id: row.id, version: row.version }),
                      onUnpublish: () => lifecycle.unpublish.mutate({ id: row.id, version: row.version }),
                      onArchive: () => lifecycle.archive.mutate({ id: row.id, version: row.version }),
                      onRestore: () => lifecycle.restore.mutate({ id: row.id, version: row.version }),
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={list.total}
        onPage={(next) => setParams({ page: next })}
      />

    </>
  );
}

/**
 * Open, closed — or neither.
 *
 * `null` means the hours are not configured, and it renders as its own state.
 * Showing "closed" there would tell a guest the restaurant is shut when nobody
 * has said so.
 */
export function OpenBadge({ isOpenNow }: { isOpenNow: boolean | null }) {
  const { t } = useTranslation();
  if (isOpenNow === null) return <Badge tone="neutral">{t('status.hoursUnknown')}</Badge>;
  return (
    <Badge tone={isOpenNow ? 'success' : 'neutral'} dot>
      {isOpenNow ? t('status.openNow') : t('status.closedNow')}
    </Badge>
  );
}

/**
 * `/branches/new` and `/branches/<id>/edit`.
 *
 * The edit route carries `/edit` because `/branches/<id>` is already the hours
 * and closures screen — the branch's *contents*, which is what a row's name
 * should lead to. The form is one of the two things you can do from there.
 */
export function BranchFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const record = useRecord<AdminBranch>(qk.branch(id ?? ''), BRANCHES_URL, id);

  if (!id) return <BranchForm branch={null} canEdit={canEdit} />;

  return (
    <RecordPage title={t('branches.editTitle')} backTo={BRANCHES_LIST} record={record}>
      {(row) => <BranchForm key={row.id} branch={row} canEdit={canEdit} />}
    </RecordPage>
  );
}

function BranchForm({ branch, canEdit }: { branch: AdminBranch | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { defaultTimezone } = useSettings();

  const [name, setName] = useState<LocalizedPair>(branch?.name ?? { ar: '', en: '' });
  const [address, setAddress] = useState<LocalizedPair>(branch?.address ?? { ar: '', en: '' });
  const [city, setCity] = useState<LocalizedPair>(editablePair(branch?.city));
  const [area, setArea] = useState<LocalizedPair>(editablePair(branch?.area));
  const [phone, setPhone] = useState(branch?.phone ?? '');
  const [latitude, setLatitude] = useState(branch?.latitude ?? '');
  const [longitude, setLongitude] = useState(branch?.longitude ?? '');
  const [mapsUrl, setMapsUrl] = useState(branch?.mapsUrl ?? '');
  const [timezone, setTimezone] = useState(branch?.timezone ?? defaultTimezone ?? '');
  const [imageId, setImageId] = useState<string | null>(branch?.image?.id ?? null);
  const [image, setImage] = useState(branch?.image ?? null);

  // Half a pair points into the sea. The API refuses it; saying so here costs
  // a round trip less.
  const halfPair = (latitude.trim() === '') !== (longitude.trim() === '');

  const dirty = useDirty({
    name, address, city, area, phone, latitude, longitude, mapsUrl, timezone, imageId,
  });

  const save = useAdminMutation(
    () => {
      const body = {
        nameAr: name.ar,
        nameEn: name.en,
        addressAr: address.ar,
        addressEn: address.en,
        cityAr: city.ar,
        cityEn: city.en,
        areaAr: area.ar,
        areaEn: area.en,
        phone: phone.trim() || undefined,
        latitude: latitude.trim() || undefined,
        longitude: longitude.trim() || undefined,
        mapsUrl: mapsUrl.trim() || undefined,
        timezone: timezone.trim() || undefined,
        imageId,
      };
      return branch
        ? apiPatch(`${BRANCHES_URL}/${branch.id}`, { ...body, expectedVersion: branch.version })
        : apiPost(BRANCHES_URL, body);
    },
    {
      successMessage: branch ? t('common.saved') : t('common.created'),
      invalidate: BRANCHES_INVALIDATE,
      onSuccess: () => navigate(BRANCHES_LIST),
    },
  );

  return (
    <FormPage
      title={branch ? t('branches.editTitle') : t('branches.newTitle')}
      description={t('branches.subtitle')}
      backTo={BRANCHES_LIST}
      dirty={dirty}
      saving={save.isPending}
      canSave={name.ar.trim().length > 0 && address.ar.trim().length > 0 && !halfPair}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
      footerStart={
        branch && (
          <Link
            to={`${BRANCHES_LIST}/${branch.id}`}
            className="dio-btn dio-btn--ghost"
            style={{ marginInlineEnd: 'auto' }}
          >
            {t('hours.title')}
          </Link>
        )
      }
    >
      {branch && <PublishBlockers translation={branch.translation} />}

      <BilingualField label={t('branches.name')} value={name} onChange={setName} required maxLength={120} disabled={!canEdit} />
      <BilingualField label={t('branches.address')} value={address} onChange={setAddress} required multiline rows={2} maxLength={300} disabled={!canEdit} />
      <OptionalBilingualField label={t('branches.city')} value={city} onChange={setCity} maxLength={100} disabled={!canEdit} />
      <OptionalBilingualField label={t('branches.area')} value={area} onChange={setArea} maxLength={100} disabled={!canEdit} />

      <Field label={t('branches.phone')} htmlFor="branch-phone">
        <Input id="branch-phone" type="tel" dir="ltr" disabled={!canEdit} value={phone} onChange={(event) => setPhone(event.target.value)} />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('branches.latitude')} htmlFor="branch-lat">
          <Input id="branch-lat" inputMode="decimal" dir="ltr" className="tabular" disabled={!canEdit} value={latitude} onChange={(event) => setLatitude(event.target.value)} />
        </Field>
        <Field label={t('branches.longitude')} htmlFor="branch-lng">
          <Input id="branch-lng" inputMode="decimal" dir="ltr" className="tabular" disabled={!canEdit} value={longitude} onChange={(event) => setLongitude(event.target.value)} />
        </Field>
      </div>
      {halfPair ? (
        <Alert tone="warning">{t('branches.coordinatesHint')}</Alert>
      ) : (
        <p className="dio-hint">{t('branches.coordinatesHint')}</p>
      )}

      <Field label={t('branches.mapsUrl')} htmlFor="branch-maps">
        <Input id="branch-maps" type="url" dir="ltr" disabled={!canEdit} value={mapsUrl} onChange={(event) => setMapsUrl(event.target.value)} />
      </Field>

      <Field label={t('branches.timezone')} htmlFor="branch-tz" hint={t('branches.timezoneHint')}>
        <Input
          id="branch-tz"
          dir="ltr"
          list="iana-zones"
          placeholder="Asia/Riyadh"
          disabled={!canEdit}
          value={timezone}
          onChange={(event) => setTimezone(event.target.value)}
        />
        {/* A suggestion list, not a closed set — zones are added and renamed,
            and the server validates against Intl rather than a hand-kept list. */}
        <datalist id="iana-zones">
          {COMMON_ZONES.map((zone) => (
            <option key={zone} value={zone} />
          ))}
        </datalist>
      </Field>

      <ImagePicker
        label={t('branches.image')}
        hint={t('branches.imageOptional')}
        surface="offer"
        value={image}
        disabled={!canEdit}
        onChange={(picked) => {
          setImageId(picked?.id ?? null);
          setImage(picked ? { ...picked, storageKey: '', alt: { ar: '', en: '' } } : null);
        }}
      />
    </FormPage>
  );
}

const COMMON_ZONES = [
  'Asia/Riyadh',
  'Asia/Dammam',
  'Asia/Damascus',
  'Asia/Beirut',
  'Asia/Amman',
  'Asia/Dubai',
  'Asia/Baghdad',
  'Africa/Cairo',
  'Europe/Istanbul',
  'UTC',
];
